import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, useToggle } from '@factorialco/deprecated-design-system'
import { CreateBenefitsChildcareRequestDocument } from 'generated/resources/graphql'
import { MyBenefitsListPageDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  BenefitsCardBenefit,
  type BenefitsCardBenefitActions,
} from 'modules/benefits/components/BenefitsCardBenefit/BenefitsCardBenefit'
import { BenefitListing, getBenefitsList } from 'modules/benefits/lib/benefitListings'

const EmployeeBenefitsList = () => {
  const [open, toggle] = useToggle(false)

  const {
    company: { id: companyId },
    employee,
  } = useCurrentSession()

  const { data } = useQuery(MyBenefitsListPageDocument, {
    variables: {
      companyId,
    },
  })

  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes

  const handleRequestBenefit = (_benefitType: string) => {
    toggle(true)()
  }

  const handleCloseModal = () => {
    toggle(false)()
  }

  const [createBenefitsChildcareRequest] = useMutation(CreateBenefitsChildcareRequestDocument, {
    extractorFn: (data) => data?.benefitsMutations?.createBenefitsChildcareRequest,
  })

  const onSubmit = async () => {
    if (!employee) {
      return
    }

    const result = await createBenefitsChildcareRequest({
      variables: {
        companyId,
        employeeId: employee.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      track(trackingEvents.benefits.requestChildcareBenefit)
      handleCloseModal()

      window.open(handled.data?.childcareRequest?.childcareUrl, '_blank', 'noopener,noreferrer')
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('benefits.flash_messages.error'),
        time: 2000,
      })
    }

    return handled
  }

  const market = employee?.legalEntity?.country
  const benefits = market ? getBenefitsList(market, featureAvailabilities) : []

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  /**
   * Determine what actions to show for a benefit.
   *
   * Right now we are generating this statically, but it will soon depend on
   * backend state.
   */
  function actionsForBenefit(benefit: BenefitListing): BenefitsCardBenefitActions | undefined {
    switch (benefit.id) {
      case 'childcare_es':
        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            onClick: () => handleRequestBenefit(benefit.id),
          },
        }
      case 'trainings_es':
        const devTrainingsEnabled = !!data?.features?.featuresConnection?.nodes?.find(
          (feature) => feature.name === 'dev_benefits_trainings'
        )?.enabled

        if (!devTrainingsEnabled || benefit.benefitStatus !== 'active') {
          return undefined
        }

        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            onClick: () =>
              navigate(
                navigation.myBenefits.benefits.request.fullPath({
                  benefitId: benefit.id,
                })
              ),
          },
        }
    }
  }

  return (
    <Box gap="s12" flexDirection="row" flexWrap="wrap">
      <Grid variant="fixed">
        {benefits.map((benefit) => {
          return (
            <Box
              key={benefit.id}
              gridColSpan={{ xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 3, xxl: 3 }}
            >
              <BenefitsCardBenefit
                benefitListing={benefit}
                showTag
                actions={actionsForBenefit(benefit)}
              />
            </Box>
          )
        })}
      </Grid>

      {open && (
        <Modal
          illustration="add-block"
          title={i18n.t('benefits.homepage.request_benefit.title')}
          description={i18n.t('benefits.homepage.request_benefit.description')}
          onClose={handleCloseModal}
          footer={
            <Button type="negative" onClick={onSubmit} iconName="external link">
              {i18n.t('common.start')}
            </Button>
          }
        />
      )}
    </Box>
  )
}

export { EmployeeBenefitsList }
